import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, filter, map, take } from 'rxjs';
import { ApplicantModel } from 'src/app/models/applicant';
import { DateTime } from 'luxon';
import { FacilitiesService } from './api/facilities.service';
import { ApplicantEtaInfo, EtaDisplayInfo } from 'src/app/types/eta';
import { ShiftsAccessor } from './accessors/shifts.accessor';

@Injectable({ providedIn: 'root' })
export class NurseEtaService {
    private readonly STALE_CHECK_INTERVAL_MS = 60000;
    private readonly STALE_MINUTES = 35;
    private readonly MINUTES_BEFORE_START_TO_SHOW_ETA = 30;
    private etaData: Map<string, Map<string, EtaDisplayInfo>> = new Map();
    private rawEtaData: Map<string, Map<string, ApplicantEtaInfo & { shiftStartTime: string }>> = new Map();
    private etaSubjects: Map<string, BehaviorSubject<Map<string, EtaDisplayInfo>>> = new Map();
    private subscriptions: Map<string, Subscription> = new Map();
    private etaTimers: Map<string, ReturnType<typeof setTimeout>> = new Map();
    private facilityId: string = '';
    private facilityTimezone: string = '';

    constructor(private facilitiesService: FacilitiesService, private shiftsAccessor: ShiftsAccessor) {}

    updateApplicantEta(shiftId: string, etaInfo: ApplicantEtaInfo, shiftStartTime?: string) {
        //not sure if we will always have shift start time or if I need to think about this more
        // If we don't have shiftStartTime bc it's from websocket event, try to get it from existing data
        if (!shiftStartTime) {
            const rawInfo = this.rawEtaData.get(shiftId);
            if (rawInfo && rawInfo.has(etaInfo.nurseId)) {
                shiftStartTime = rawInfo.get(etaInfo.nurseId)!.shiftStartTime;
            } 
            else { //get it from shift data because the web socket just came in and no shift data yet
                this.shiftsAccessor.shift$(shiftId).pipe(
                        filter((fetchedShift) => !!fetchedShift),
                        take(1), 
                        map((fetchedShift) => fetchedShift.shiftData?.shiftFrom))
                    .subscribe((shiftFrom) => {
                        if (shiftFrom) {
                            const shiftStart = DateTime.fromISO(shiftFrom, { zone: this.facilityTimezone });
                            if (shiftStart) {
                                this.updateApplicantEta(shiftId, etaInfo, shiftStart.toISO());
                            }
                        }
                    });
                return;
            }
        }

        let rawInfo = this.rawEtaData.get(shiftId) || new Map();
        rawInfo.set(etaInfo.nurseId, { ...etaInfo, shiftStartTime });
        this.rawEtaData.set(shiftId, rawInfo);
        
        let info = this.etaData.get(shiftId) || new Map<string, EtaDisplayInfo>();
        info.set(etaInfo.nurseId, this.getDisplayInfo(etaInfo, shiftStartTime));
        this.etaData.set(shiftId, info);
        if (this.etaSubjects.has(shiftId)) {
            this.etaSubjects.get(shiftId)!.next(new Map(info));
        }
        
        // Set timer for stale data check
        if (!this.etaTimers.has(`${shiftId}_staleCheck`)) {
            const timer = setInterval(() => this.checkStaleData(shiftId), 60000);
            this.etaTimers.set(`${shiftId}_staleCheck`, timer);
        }
    }

    private getDisplayInfo(applicant: ApplicantEtaInfo, shiftStartTime: string, facilityTimezone?: string): EtaDisplayInfo {
        const timezone = facilityTimezone || this.facilityTimezone;
        // If within clockin radius, show "On site"
        if (applicant.isWithinFacilityClockInDistance) {
            return {
                display: 'On site',
                rawEta: applicant.eta,
                pingTime: applicant.pingTime,
                isWithinFacilityClockInDistance: true,
                iconColor: 'green'
            };
        }
        // If no ETA or lastUpdated, show "ETA unavailable"
        if (!applicant.eta && !applicant.pingTime) {
            return { display: 'ETA unavailable', iconColor: 'grey' };
        }
        // If lastUpdated is older than 35 minutes, show "ETA unavailable"
        const lastUpdated = DateTime.fromISO(applicant.pingTime ?? applicant.eta, { zone: timezone });
        const now = DateTime.now().setZone(timezone);
        const minutesSinceLastUpdate = now.diff(lastUpdated, 'minutes').minutes;
        if (minutesSinceLastUpdate > this.STALE_MINUTES) {
            return { display: 'ETA unavailable', iconColor: 'grey' };
        }
        // If ETA is earlier than current time, show "ETA unavailable"
        const etaDate = DateTime.fromISO(applicant.eta, { zone: timezone });
        if (etaDate < now) {
            return { display: 'ETA unavailable', iconColor: 'grey' };
        }

        const shiftStart = DateTime.fromISO(shiftStartTime, { zone: timezone });
        const minutesAfterShiftStart = etaDate.diff(shiftStart, 'minutes').minutes;

        // IF ETA is more than 1 hour after the scheduled shift start time show "ETA unavailable"
        if (minutesAfterShiftStart > 60) {
            return { display: 'ETA unavailable', iconColor: 'grey' };
        }
        
        // Otherwise, show ETA time and calculate icon color
        const iconColor = minutesAfterShiftStart <= 30 ? 'green' : 'orange';
        
        const etaStr = etaDate.toFormat('h:mm a');
        return {
            display: 'ETA: ' + etaStr,
            rawEta: applicant.eta,
            pingTime: applicant.pingTime || null,
            isWithinFacilityClockInDistance: false,
            iconColor
        };
    }

    cleanupShift(shiftId: string) {
        if (this.subscriptions.has(shiftId)) {
            this.subscriptions.get(shiftId)!.unsubscribe();
            this.subscriptions.delete(shiftId);
        }
        this.clearTimers(shiftId);
        this.etaSubjects.delete(shiftId);
        this.etaData.delete(shiftId);
        this.rawEtaData.delete(shiftId);
    }

    /**
     * Check if any ETA data has become stale and update display accordingly
     */
    private checkStaleData(shiftId: string) {
        const rawInfo = this.rawEtaData.get(shiftId);
        if (!rawInfo) return;
        
        let hasChanges = false;
        const updatedDisplayInfo = new Map<string, EtaDisplayInfo>();
        
        rawInfo.forEach((data, applicantId) => {
            const newDisplayInfo = this.getDisplayInfo(data, data.shiftStartTime);
            updatedDisplayInfo.set(applicantId, newDisplayInfo);
            
            const currentDisplayInfo = this.etaData.get(shiftId)?.get(applicantId);
            if (currentDisplayInfo?.display !== newDisplayInfo.display) {
                hasChanges = true;
            }
        });
        
        if (hasChanges) {
            this.etaData.set(shiftId, updatedDisplayInfo);
            if (this.etaSubjects.has(shiftId)) {
                this.etaSubjects.get(shiftId)!.next(new Map(updatedDisplayInfo));
            }
        }
    }


    /**
     * Loads ETA for a shift, but only starts fetching ETA data within 30 minutes of shift start.
     * Clears ETA data at shift end.
     */
    loadEtaForShift(shiftId: string, shiftStartTime: DateTime, shiftEndTime: DateTime, applicants: ApplicantModel[], facilityId: string, facilityTimezone: string) {
        this.facilityId = facilityId;
        this.facilityTimezone = facilityTimezone;
        
        const now = DateTime.now().setZone(this.facilityTimezone);
        const thirtyBeforeStart = shiftStartTime.minus({ minutes: this.MINUTES_BEFORE_START_TO_SHOW_ETA });
        const msToThirtyBeforeStart = thirtyBeforeStart.diff(now).milliseconds;

        const msToShiftEnd = shiftEndTime.diff(now).milliseconds;

        // Clear any existing timers and data first
        this.clearTimers(shiftId);
        this.etaData.delete(shiftId);
        this.rawEtaData.delete(shiftId);

        if (msToThirtyBeforeStart > 0) {
            // Too early, set a timer to start fetching ETA 30 min before shift start
            const preStartTimer = setTimeout(() => {
                this.fetchAndStartEta(shiftId, shiftStartTime, applicants);
            }, msToThirtyBeforeStart);
            this.etaTimers.set(`${shiftId}_preStart`, preStartTimer);

            // Also set a timer to clear at shift end
            const endTimer = setTimeout(() => {
                this.clearEtaForShift(shiftId);
            }, msToShiftEnd);
            this.etaTimers.set(`${shiftId}_end`, endTimer);

            // Notify subscribers with empty map
            if (this.etaSubjects.has(shiftId)) {
                this.etaSubjects.get(shiftId)!.next(new Map());
            }
        } else {
            // Within 30 minutes, start fetching ETA now
            this.fetchAndStartEta(shiftId, shiftStartTime, applicants);

            // Set a timer to clear at shift end
            const endTimer = setTimeout(() => {
                this.clearEtaForShift(shiftId);
            }, msToShiftEnd);
            this.etaTimers.set(`${shiftId}_end`, endTimer);
        }
    }

    private fetchAndStartEta(shiftId: string, shiftStart: DateTime, applicants: ApplicantModel[] = []) {
        this.getEtaData(applicants).subscribe(etaData => {
            const applicantMap = new Map<string, EtaDisplayInfo>();
            const rawApplicantMap = new Map<string, ApplicantEtaInfo & { shiftStartTime: string }>();
            const shiftStartTime = shiftStart.toISO();

            // Create a map of nurse IDs from the response for quick lookup
            const responseNurseIds = new Set(etaData.map(app => app.nurseId));

            // Process all ETA data from the response
            etaData.forEach(app => {
                rawApplicantMap.set(app.nurseId, { ...app, shiftStartTime });
                applicantMap.set(app.nurseId, this.getDisplayInfo(app, shiftStartTime));
            });

            // For any applicants not in the response, create "ETA unavailable" entries
            applicants.forEach(applicant => {
                let info: ApplicantEtaInfo;
                if (!responseNurseIds.has(applicant.user._id)) {
                    info = {
                        nurseId: applicant.user._id,
                        eta: applicant.eta ? applicant.eta : null, // if eta shared by nurse, use it, otherwise null
                        pingTime: null,
                        isWithinFacilityClockInDistance: false
                    };
                    rawApplicantMap.set(applicant.user._id, { ...info, shiftStartTime });
                    applicantMap.set(applicant.user._id, this.getDisplayInfo(info, shiftStartTime));
                }
            });

            this.rawEtaData.set(shiftId, rawApplicantMap);
            this.etaData.set(shiftId, applicantMap);

            if (!this.etaSubjects.has(shiftId)) {
                this.etaSubjects.set(shiftId, new BehaviorSubject(applicantMap));
            } else {
                this.etaSubjects.get(shiftId)!.next(applicantMap);
            }

            // Start the stale check timer
            if (!this.etaTimers.has(`${shiftId}_staleCheck`)) {
                const timer = setInterval(() => this.checkStaleData(shiftId), this.STALE_CHECK_INTERVAL_MS);
                this.etaTimers.set(`${shiftId}_staleCheck`, timer);
            }
        });
    }

    private clearEtaForShift(shiftId: string) {
        this.etaData.delete(shiftId);
        this.rawEtaData.delete(shiftId);
        this.clearTimers(shiftId);
        if (this.etaSubjects.has(shiftId)) {
            this.etaSubjects.get(shiftId)!.next(new Map());
        }
    }

    private clearTimers(shiftId: string) {
        const preStartTimer = this.etaTimers.get(`${shiftId}_preStart`);
        if (preStartTimer) {
            clearTimeout(preStartTimer);
            this.etaTimers.delete(`${shiftId}_preStart`);
        }
        const endTimer = this.etaTimers.get(`${shiftId}_end`);
        if (endTimer) {
            clearTimeout(endTimer);
            this.etaTimers.delete(`${shiftId}_end`);
        }
        const staleCheckTimer = this.etaTimers.get(`${shiftId}_staleCheck`);
        if (staleCheckTimer) {
            clearInterval(staleCheckTimer);
            this.etaTimers.delete(`${shiftId}_staleCheck`);
        }
    }


    getEtaData(applicants: ApplicantModel[]): Observable<ApplicantEtaInfo[]> {
        return this.facilitiesService.fetchNursesGeoLocation(this.facilityId, applicants.map(a => a.user._id));
    }

    getEtaMap$(shiftId: string): Observable<Map<string, EtaDisplayInfo>> {
        if (!this.etaSubjects.has(shiftId)) {
            this.etaSubjects.set(shiftId, new BehaviorSubject<Map<string, EtaDisplayInfo>>(new Map()));
        }

        return this.etaSubjects.get(shiftId)!.asObservable();
    }
}